import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AiExecutionSettings, type AiExecutionActions, type AiExecutionView, type DeviceLogin } from "@studio-features/meta-crew/ai-execution";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { codexKeys, useCodexStatus, useRunners } from "../lib/codex-api";
import { fmtDateTime } from "../lib/format";
import { llmInvalidate, useAiKeySettings } from "../lib/llm-api";
import type { Me } from "../lib/types";
import { Loading, PanelError } from "../console/ui";

/**
 * 시스템 설정 › AI 실행 방식 (요청서 30절, 시안 388082b AiExecutionSettings). docs/meta-crew-manuscript-refine-design.md 13·13-8절.
 * API 키 / GPT 플랜(실행기의 Codex) 선택, 실행기 카드, device-auth 로그인(주소·코드는 이 화면 세션만 가진다), 플랜 모델·추론 강도.
 * 상태는 서버(실행기 신호)를 그대로 보이고, 승인 여부도 서버가 알려 준다 (화면이 성공을 만들지 않는다).
 */
export function AiExecutionScreen({ me, onOpenKeys }: { me: Me; onOpenKeys?: () => void }) {
  const qc = useQueryClient();
  const canRead = can(me, "SYSTEM_READ");
  const canManage = can(me, "LLM_KEY_MANAGE");
  // 이 화면에서 시작한 로그인만 코드·주소를 가진다. 다른 관리자가 진행 중이면 pendingLogin 만 보인다.
  const [session, setSession] = useState<DeviceLogin | null>(null);
  const status = useCodexStatus(canRead, !!session);
  const runners = useRunners(canRead);
  const { keys } = useAiKeySettings();
  if (!canRead) return <PanelError error="권한이 없습니다." />;
  if (status.error) return <PanelError error={errorMessage(status.error)} onRetry={() => void status.refetch()} />;
  if (!status.data || !runners.data) return <Loading />;
  const s = status.data;
  const runnerList = runners.data.items;
  const runner = runnerList.find((r) => r.status === "online") ?? runnerList[0] ?? null;
  const pending = s.login;
  // 승인되면 세션을 비운다 (시안이 승인 창을 닫는다). 서버의 진행 중 로그인이 사라졌는데 로그인이 안 됐으면 만료·취소.
  const loginResult: AiExecutionView["loginResult"] = !pending && s.lastLogin && !s.lastLogin.ok ? (/취소/.test(s.lastLogin.error ?? "") ? "cancelled" : "expired") : undefined;
  if (session && (s.loggedIn || (!pending && loginResult))) {
    // 렌더 중 setState 대신 다음 틱에
    queueMicrotask(() => setSession(null));
  }
  const refresh = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: codexKeys.status }), qc.invalidateQueries({ queryKey: codexKeys.runners }), ...llmInvalidate.map((k) => qc.invalidateQueries({ queryKey: k }))]);
  };
  const activeKeys = (keys.data?.keys ?? []).filter((k) => k.status === "active").length;
  const view: AiExecutionView = {
    // runner: 목록이 비면 미배포(null), 있으면 연결 상태 그대로 (끊김은 offline)
    runner: runner
      ? { id: runner.id, name: runner.name, status: runner.status, version: runner.codexVersion, lastSeenAt: fmtDateTime(runner.lastSeenAt), running: runner.running, concurrency: runner.concurrency }
      : null,
    provider: s.provider,
    installed: s.installed,
    version: s.version,
    loggedIn: s.loggedIn,
    checking: status.isFetching && !status.data,
    account: undefined,
    checkedAt: s.lastCheckedAt ? fmtDateTime(s.lastCheckedAt) : "—",
    models: { billing: "plan", default: s.model || s.defaultModel || "", allowed: s.models.map((m) => m.slug) },
    model: s.model,
    effort: s.effort,
    pendingLogin: !!pending && !session,
    loginResult,
    canManage,
    api: { keys: activeKeys, defaultModel: keys.data?.models.default ?? "" },
    error: s.error ?? undefined,
  };
  const after = async <T,>(p: Promise<T>): Promise<T> => {
    try {
      return await p;
    } finally {
      await refresh();
    }
  };
  const actions: AiExecutionActions = {
    save: async (patch) => {
      const keysOf: Record<string, string> = { provider: "llm_refine_provider", model: "llm_codex_model", effort: "llm_codex_effort" };
      await after(
        (async () => {
          for (const [k, v] of Object.entries(patch)) if (v !== undefined) await api.put(`/admin/settings/${keysOf[k]}`, { value: v });
        })(),
      );
    },
    refresh,
    login: async () => {
      const r = await after(api.post<DeviceLogin & { runner: { id: string; name: string } }>("/admin/llm/codex/login", runner ? { runnerId: runner.id } : {}));
      const login = { url: r.url, code: r.code, expiresAt: r.expiresAt };
      setSession(login);
      return login;
    },
    cancelLogin: async () => {
      await after(api.post("/admin/llm/codex/login/cancel", runner ? { runnerId: runner.id } : {}));
      setSession(null);
    },
    logout: async () => {
      const r = await after(api.post<{ ok: boolean; error: string | null }>("/admin/llm/codex/logout", runner ? { runnerId: runner.id } : {}));
      if (!r.ok) throw new Error(r.error ?? "로그아웃하지 못했습니다.");
    },
    openKeys: onOpenKeys,
  };
  return <AiExecutionSettings key={runner?.id ?? "none"} view={view} actions={actions} initialLogin={session ?? undefined} />;
}
